/* ==========================================================================
   Blink Box - PDP dynamic checkout analytics
   Markup source: sections/main-product.liquid
========================================================================== */
(function () {
  'use strict';

  if (window.__blinkboxPdpDynamicCheckoutAnalyticsLoaded) return;
  window.__blinkboxPdpDynamicCheckoutAnalyticsLoaded = true;

  const ROOT = '[data-pdp-dynamic-checkout]';
  const PDP = '.cc-main-product';
  const ACTIVATION_DEDUPE_MS = 1500;
  const ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;
  const ATTEMPT_KEY = 'blinkbox_pdp_dynamic_checkout_attempt_v1';

  const METHOD_BY_TAG = {
    'SHOP-PAY-WALLET-BUTTON': 'shop_pay',
    'SHOPIFY-AMAZON-PAY-BUTTON': 'amazon_pay',
    'SHOPIFY-APPLE-PAY-BUTTON': 'apple_pay',
    'SHOPIFY-GOOGLE-PAY-BUTTON': 'google_pay',
    'SHOPIFY-PAYPAL-BUTTON': 'paypal'
  };

  const initialised = new WeakSet();
  const viewed = new WeakSet();
  const viewTimers = new WeakMap();
  const lastActivation = new WeakMap();

  function analyticsProcessingAllowed() {
    const check =
      window.Shopify?.customerPrivacy?.analyticsProcessingAllowed;

    if (typeof check !== 'function') return true;

    try {
      return check.call(window.Shopify.customerPrivacy) !== false;
    } catch (error) {
      return true;
    }
  }

  function publishShopifyEvent(name, payload) {
    if (!analyticsProcessingAllowed()) return;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    try {
      const result = window.Shopify.analytics.publish(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      // Analytics must never interrupt the shopping experience.
    }
  }

  function sendGoogleEvent(name, params) {
    if (!analyticsProcessingAllowed()) return;
    if (typeof window.gtag !== 'function') return;

    try {
      window.gtag('event', name, params);
    } catch (error) {
      // Google measurement is optional.
    }
  }

  function readAttempt() {
    if (!analyticsProcessingAllowed()) return null;

    try {
      const raw = window.localStorage.getItem(ATTEMPT_KEY);
      if (!raw) return null;

      const attempt = JSON.parse(raw);
      const clickedAt = Number(attempt?.clicked_at_ms || 0);

      if (!clickedAt || Date.now() - clickedAt > ATTEMPT_TTL_MS) {
        window.localStorage.removeItem(ATTEMPT_KEY);
        return null;
      }

      return attempt;
    } catch (error) {
      return null;
    }
  }

  function writeAttempt(attempt) {
    if (!analyticsProcessingAllowed()) return;

    try {
      window.localStorage.setItem(
        ATTEMPT_KEY,
        JSON.stringify(attempt)
      );
    } catch (error) {
      // Storage is optional.
    }
  }

  function normaliseMethod(value) {
    const text = String(value || '').toLowerCase();

    if (
      text.includes('shop_pay') ||
      text.includes('shop-pay') ||
      text.includes('shopify_pay')
    ) return 'shop_pay';

    if (text.includes('apple_pay') || text.includes('apple-pay')) {
      return 'apple_pay';
    }

    if (text.includes('google_pay') || text.includes('google-pay')) {
      return 'google_pay';
    }

    if (text.includes('amazon_pay') || text.includes('amazon-pay')) {
      return 'amazon_pay';
    }

    if (text.includes('paypal')) return 'paypal';

    if (
      text.includes('buy_it_now') ||
      text.includes('buy-it-now')
    ) return 'buy_it_now';

    return '';
  }

  function getDisplayedMethod(root) {
    for (const [tagName, method] of Object.entries(METHOD_BY_TAG)) {
      if (root.querySelector(tagName.toLowerCase())) return method;
    }

    const host = root.querySelector('shopify-accelerated-checkout');
    const recommended = host?.getAttribute('recommended');
    const method = normaliseMethod(recommended);

    return method || 'unknown';
  }

  function getClickedMethod(event) {
    const path = typeof event.composedPath === 'function'
      ? event.composedPath()
      : [event.target];

    let acceleratedCheckoutHostSeen = false;

    for (const node of path) {
      if (!(node instanceof Element)) continue;

      if (METHOD_BY_TAG[node.tagName]) {
        return METHOD_BY_TAG[node.tagName];
      }

      if (node.matches?.('.shopify-payment-button__button--unbranded')) {
        return 'buy_it_now';
      }

      if (node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT') {
        acceleratedCheckoutHostSeen = true;
      }
    }

    return acceleratedCheckoutHostSeen
      ? 'more_payment_options'
      : '';
  }

  function getRootFromEvent(event) {
    const path = typeof event.composedPath === 'function'
      ? event.composedPath()
      : [event.target];

    for (const node of path) {
      if (!(node instanceof Element)) continue;

      if (node.matches?.(ROOT) && node.closest(PDP)) {
        return node;
      }
    }

    return null;
  }

  function getForm(root) {
    const form = root.closest('form.js-product-form-main');

    return form && form.closest(PDP)
      ? form
      : null;
  }

  function buildPayload(root, checkoutMethod) {
    const form = getForm(root);

    const variantId =
      form?.querySelector('input[name="id"]')?.value || '';

    const quantity = Math.max(
      Number(
        form?.querySelector('input[name="quantity"]')?.value || 1
      ),
      1
    );

    return {
      checkout_method: checkoutMethod || 'unknown',

      source_product_id:
        root.dataset.sourceProductId || '',

      source_product_name:
        root.dataset.sourceProductName || '',

      source_product_handle:
        root.dataset.sourceProductHandle || '',

      vendor:
        root.dataset.sourceProductVendor || '',

      variant_id: variantId,
      quantity
    };
  }

  function createAttemptId() {
    if (typeof window.crypto?.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return [
      Date.now().toString(36),
      Math.random().toString(36).slice(2, 10)
    ].join('-');
  }

  function createCheckoutAttempt(root, checkoutMethod) {
    return {
      ...buildPayload(root, checkoutMethod),
      attempt_id: createAttemptId(),
      checkout_entry_point: 'pdp_dynamic',
      pdp_path: window.location.pathname,
      clicked_at: new Date().toISOString(),
      clicked_at_ms: Date.now(),
      departed_at_ms: 0,
      returned_at_ms: 0,
      return_location: '',
      return_delay_bucket: '',
      reengaged: false,
      reengagement_action: ''
    };
  }

  function saveCheckoutAttempt(root, checkoutMethod) {
    const attempt = createCheckoutAttempt(root, checkoutMethod);

    writeAttempt(attempt);

    publishShopifyEvent(
      'blinkbox:pdp_dynamic_checkout_attempt',
      attempt
    );
  }

  function track(root, eventName, checkoutMethod) {
    const payload = buildPayload(
      root,
      checkoutMethod
    );

    publishShopifyEvent(
      `blinkbox_${eventName}`,
      payload
    );

    sendGoogleEvent(
      eventName,
      payload
    );
  }

  function trackView(root) {
    if (viewed.has(root)) return;

    const timer = viewTimers.get(root);

    if (timer) {
      window.clearTimeout(timer);
    }

    viewTimers.delete(root);
    viewed.add(root);

    track(
      root,
      'pdp_dynamic_checkout_view',
      getDisplayedMethod(root)
    );
  }

  function initViewTracking(root) {
    if (!('IntersectionObserver' in window)) return;

    const target =
      root.querySelector('shopify-accelerated-checkout') ||
      root;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (viewed.has(root)) {
            observer.disconnect();
            return;
          }

          if (entry.intersectionRatio >= 0.5) {
            if (viewTimers.has(root)) return;

            viewTimers.set(
              root,
              window.setTimeout(() => {
                trackView(root);
                observer.disconnect();
              }, 250)
            );
          } else {
            const timer =
              viewTimers.get(root);

            if (timer) {
              window.clearTimeout(timer);
            }

            viewTimers.delete(root);
          }
        });
      },
      {
        threshold: [0, 0.5]
      }
    );

    observer.observe(target);
  }

  function init(root) {
    if (
      !root ||
      initialised.has(root) ||
      !getForm(root)
    ) return;

    initialised.add(root);

    root.dataset
      .pdpDynamicCheckoutAnalyticsInitialised =
      'true';

    initViewTracking(root);
  }

  function boot(scope) {
    const container =
      scope || document;

    container
      .querySelectorAll?.(`${PDP} ${ROOT}`)
      .forEach(init);
  }

  function isDuplicateActivation(root, method) {
    const now = Date.now();
    const previous = lastActivation.get(root);

    if (
      previous &&
      previous.method === method &&
      now - previous.at < ACTIVATION_DEDUPE_MS
    ) {
      return true;
    }

    lastActivation.set(root, {
      method,
      at: now
    });

    return false;
  }

  function getReturnDelayBucket(delayMs) {
    if (delayMs < 15 * 1000) return 'under_15s';
    if (delayMs < 60 * 1000) return '15_60s';
    if (delayMs < 5 * 60 * 1000) return '1_5m';
    if (delayMs < 30 * 60 * 1000) return '5_30m';
    return '30m_plus';
  }

  function getReturnLocation(attempt) {
    const path = window.location.pathname;

    if (path === attempt.pdp_path) return 'same_pdp';
    if (path.startsWith('/products/')) return 'different_pdp';
    if (path.startsWith('/collections/')) return 'collection';
    if (path === '/cart' || path.startsWith('/cart/')) return 'cart';
    if (path === '/') return 'home';

    return 'other';
  }

  function markCheckoutDeparture() {
    const attempt = readAttempt();

    if (
      !attempt ||
      attempt.returned_at_ms ||
      attempt.departed_at_ms
    ) return;

    attempt.departed_at_ms = Date.now();
    writeAttempt(attempt);
  }

  function detectCheckoutReturn() {
    const attempt = readAttempt();

    if (
      !attempt ||
      attempt.returned_at_ms ||
      !attempt.departed_at_ms
    ) return;

    const now = Date.now();
    const returnDelay = Math.max(
      now - Number(attempt.departed_at_ms || attempt.clicked_at_ms),
      0
    );

    attempt.returned_at_ms = now;
    attempt.return_location = getReturnLocation(attempt);
    attempt.return_delay_bucket = getReturnDelayBucket(returnDelay);

    writeAttempt(attempt);

    const payload = {
      checkout_method: attempt.checkout_method || 'unknown',
      checkout_entry_point: 'pdp_dynamic',
      source_product_id: attempt.source_product_id || '',
      source_product_name: attempt.source_product_name || '',
      source_product_handle: attempt.source_product_handle || '',
      vendor: attempt.vendor || '',
      variant_id: attempt.variant_id || '',
      quantity: Number(attempt.quantity || 1),
      return_location: attempt.return_location,
      return_delay_bucket: attempt.return_delay_bucket
    };

    publishShopifyEvent(
      'blinkbox_pdp_dynamic_checkout_return',
      payload
    );

    sendGoogleEvent(
      'pdp_dynamic_checkout_return',
      payload
    );
  }

  function trackReengagement(action) {
    const attempt = readAttempt();

    if (
      !attempt ||
      !attempt.returned_at_ms ||
      attempt.reengaged ||
      !action
    ) return;

    attempt.reengaged = true;
    attempt.reengagement_action = action;
    writeAttempt(attempt);

    const payload = {
      checkout_method: attempt.checkout_method || 'unknown',
      checkout_entry_point: 'pdp_dynamic',
      source_product_id: attempt.source_product_id || '',
      source_product_name: attempt.source_product_name || '',
      source_product_handle: attempt.source_product_handle || '',
      vendor: attempt.vendor || '',
      variant_id: attempt.variant_id || '',
      quantity: Number(attempt.quantity || 1),
      return_location: attempt.return_location || '',
      return_delay_bucket: attempt.return_delay_bucket || '',
      reengagement_action: action
    };

    publishShopifyEvent(
      'blinkbox_pdp_dynamic_checkout_reengage',
      payload
    );

    sendGoogleEvent(
      'pdp_dynamic_checkout_reengage',
      payload
    );
  }

  function getReengagementActionFromClick(event) {
    const target = event.target instanceof Element
      ? event.target
      : null;

    if (!target) return '';

    const checkoutControl = target.closest(
      'button[name="checkout"], input[name="checkout"], a[href^="/checkout"]'
    );

    if (checkoutControl) return 'checkout_again';

    const cartLink = target.closest('a[href="/cart"], a[href^="/cart?"]');
    if (cartLink) return 'view_cart';

    const productLink = target.closest('a[href^="/products/"]');
    if (
      productLink &&
      productLink.pathname !== window.location.pathname
    ) {
      return 'view_another_product';
    }

    return '';
  }

  function handleReengagementClick(event) {
    const action = getReengagementActionFromClick(event);
    if (action) trackReengagement(action);
  }

  function handleReengagementSubmit(event) {
    const form = event.target instanceof HTMLFormElement
      ? event.target
      : null;

    if (!form) return;

    const action = String(form.getAttribute('action') || '');

    if (
      form.matches('.js-product-form-main') ||
      action.includes('/cart/add')
    ) {
      trackReengagement('add_to_cart');
    }
  }

  function handleDynamicCheckoutActivation(event) {
    const root = getRootFromEvent(event);

    if (
      !root ||
      !initialised.has(root)
    ) return;

    const method = getClickedMethod(event);

    if (!method || isDuplicateActivation(root, method)) return;

    const previousAttempt = readAttempt();

    if (
      previousAttempt?.returned_at_ms &&
      !previousAttempt.reengaged
    ) {
      trackReengagement('dynamic_checkout_again');
    }

    if (!viewed.has(root)) {
      trackView(root);
    }

    track(
      root,
      'pdp_dynamic_checkout_click',
      method
    );

    if (method === 'more_payment_options') {
      track(
        root,
        'pdp_more_payment_options_click',
        method
      );
    }

    saveCheckoutAttempt(root, method);
  }

  document.addEventListener(
    'pointerdown',
    handleDynamicCheckoutActivation,
    true
  );

  document.addEventListener(
    'click',
    handleDynamicCheckoutActivation,
    true
  );

  document.addEventListener(
    'click',
    handleReengagementClick,
    true
  );

  document.addEventListener(
    'submit',
    handleReengagementSubmit,
    true
  );

  window.addEventListener(
    'pagehide',
    markCheckoutDeparture
  );

  window.addEventListener(
    'blur',
    markCheckoutDeparture
  );

  window.addEventListener(
    'focus',
    detectCheckoutReturn
  );

  window.addEventListener(
    'pageshow',
    detectCheckoutReturn
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') {
        markCheckoutDeparture();
      } else if (document.visibilityState === 'visible') {
        detectCheckoutReturn();
      }
    }
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        boot(document);
        detectCheckoutReturn();
      },
      { once: true }
    );
  } else {
    boot(document);
    detectCheckoutReturn();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      boot(event.target);
    }
  );
})();
